/**
 * useProjectState - Project persistence state management
 *
 * Manages all project-related states including:
 * - Current project ID and status
 * - Save state tracking
 * - GeoTIFF cache references
 * - Building insights references
 * - Address and RPU tracking
 */

import { useState, useCallback } from 'react';

export type ProjectStatus = 'draft' | 'final';

export interface ProjectState {
  // Project identification
  currentProjectId: string | null;
  projectStatus: ProjectStatus;

  // Save state
  isSaving: boolean;
  lastSaved: Date | null;

  // External references
  geotiffCacheId: string | null;
  buildingInsightsId: string | null;

  // Location data
  currentRPU: string | null;
  currentAddress: string;
}

export interface ProjectStateActions {
  // Project setters
  setCurrentProjectId: (id: string | null) => void;
  setProjectStatus: (status: ProjectStatus) => void;

  // Save state setters
  setIsSaving: (saving: boolean) => void;
  setLastSaved: (date: Date | null) => void;

  // External reference setters
  setGeotiffCacheId: (id: string | null) => void;
  setBuildingInsightsId: (id: string | null) => void;

  // Location setters
  setCurrentRPU: (rpu: string | null) => void;
  setCurrentAddress: (address: string) => void;

  // Convenience methods
  startSaving: () => void;
  finishSaving: () => void;
  markAsFinal: () => void;
  markAsDraft: () => void;
  clearProject: () => void;
  updateProjectReferences: (refs: {
    geotiffCacheId?: string;
    buildingInsightsId?: string;
    rpu?: string;
    address?: string;
  }) => void;
}

export function useProjectState(): ProjectState & ProjectStateActions {
  // Project identification
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(null);
  const [projectStatus, setProjectStatus] = useState<ProjectStatus>('draft');

  // Save state
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);

  // External references
  const [geotiffCacheId, setGeotiffCacheId] = useState<string | null>(null);
  const [buildingInsightsId, setBuildingInsightsId] = useState<string | null>(null);

  // Location data
  const [currentRPU, setCurrentRPU] = useState<string | null>(null);
  const [currentAddress, setCurrentAddress] = useState<string>('');

  // Convenience methods
  const startSaving = useCallback(() => {
    setIsSaving(true);
  }, []);

  const finishSaving = useCallback(() => {
    setIsSaving(false);
    setLastSaved(new Date());
  }, []);

  const markAsFinal = useCallback(() => {
    setProjectStatus('final');
  }, []);

  const markAsDraft = useCallback(() => {
    setProjectStatus('draft');
  }, []);

  const clearProject = useCallback(() => {
    setCurrentProjectId(null);
    setProjectStatus('draft');
    setIsSaving(false);
    setLastSaved(null);
    setGeotiffCacheId(null);
    setBuildingInsightsId(null);
    setCurrentRPU(null);
    setCurrentAddress('');
  }, []);

  const updateProjectReferences = useCallback((refs: {
    geotiffCacheId?: string;
    buildingInsightsId?: string;
    rpu?: string;
    address?: string;
  }) => {
    if (refs.geotiffCacheId !== undefined) setGeotiffCacheId(refs.geotiffCacheId);
    if (refs.buildingInsightsId !== undefined) setBuildingInsightsId(refs.buildingInsightsId);
    if (refs.rpu !== undefined) setCurrentRPU(refs.rpu);
    if (refs.address !== undefined) setCurrentAddress(refs.address);
  }, []);

  return {
    // State
    currentProjectId,
    projectStatus,
    isSaving,
    lastSaved,
    geotiffCacheId,
    buildingInsightsId,
    currentRPU,
    currentAddress,

    // Setters
    setCurrentProjectId,
    setProjectStatus,
    setIsSaving,
    setLastSaved,
    setGeotiffCacheId,
    setBuildingInsightsId,
    setCurrentRPU,
    setCurrentAddress,

    // Convenience methods
    startSaving,
    finishSaving,
    markAsFinal,
    markAsDraft,
    clearProject,
    updateProjectReferences,
  };
}

export default useProjectState;
